import type { CSSProperties, RefObject } from 'react';

/** Visually hidden; keeps the node in the layout tree for aria-live. */
export const offscreenLiveRegionStyle: CSSProperties = {
  border: 0,
  clip: 'rect(1px, 1px, 1px, 1px)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

export function getListOptionAnnounceText(option: {
  label?: unknown;
  searchValue?: string | number;
  value?: unknown;
}): string {
  if (option.searchValue != null && String(option.searchValue).length > 0) {
    return String(option.searchValue);
  }
  if (typeof option.label === 'string' || typeof option.label === 'number') {
    return String(option.label);
  }
  return String(option.value ?? '');
}

export type AnnouncePoliteOptions = {
  /** Interrupts current speech; use sparingly (e.g. after a control that otherwise masks polite regions). */
  assertive?: boolean;
};

/** Clears then sets text so SRs pick up updates (including repeated removals). */
export function announcePolite(
  liveRegionRef: RefObject<HTMLElement | null>,
  message: string,
  options?: AnnouncePoliteOptions
): void {
  const el = liveRegionRef.current;
  if (!el) return;
  const trimmed = message.trim();
  if (!trimmed) return;

  const useAssertive = options?.assertive === true;
  const previousLive = el.getAttribute('aria-live');
  if (useAssertive) {
    el.setAttribute('aria-live', 'assertive');
  }

  el.textContent = '';
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      el.textContent = trimmed;
      const clearAfterMs = useAssertive ? 1200 : 750;
      window.setTimeout(() => {
        if (liveRegionRef.current !== el) return;
        el.textContent = '';
        if (useAssertive) {
          el.setAttribute('aria-live', previousLive ?? 'polite');
        }
      }, clearAfterMs);
    });
  });
}
